'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Testimonial {
  id: string | number;
  name: string;
  role?: string;
  area: string;
  image?: string;
  alt?: string;
  rating: number;
  comment: string;
  date: string;
}

interface TestimonialsProps {
  testimonials: Testimonial[];
}

const DEFAULT_AVATAR = '/assets/default-avatar.svg';

const Testimonials = ({ testimonials }: TestimonialsProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  useEffect(() => {
    if (!isHydrated) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isHydrated, testimonials.length]);

  if (!isHydrated) {
    return (
      <section className="bg-gradient-to-br from-primary/5 to-secondary/5 py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-textPrimary mb-4">
              APA KATA PELANGGAN KAMI
            </h2>
            <p className="text-lg font-body text-textSecondary">
              Testimoni sebenar daripada pelanggan yang telah menggunakan servis Surilipat.
            </p>
            <p className="text-lg font-body text-textSecondary">
              Kami menitikberatkan kualiti, kemasan dan komunikasi yang baik antara pelanggan dan
              ejen.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-card rounded-2xl shadow-xl p-8 md:p-12">
              <div className="h-32 bg-muted rounded animate-pulse"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const activeTestimonial = testimonials[activeIndex];

  return (
    <section className="bg-gradient-to-br from-primary/5 to-secondary/5 py-16">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-textPrimary mb-4">
            APA KATA PELANGGAN KAMI
          </h2>
          <p className="text-lg font-body text-textSecondary">
            Testimoni sebenar daripada pelanggan yang telah menggunakan servis Surilipat.
          </p>
          <p className="text-lg font-body text-textSecondary">
            Kami menitikberatkan kualiti, kemasan dan komunikasi yang baik antara pelanggan dan
            ejen.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-card rounded-2xl shadow-xl p-8 md:p-12 relative">
            <Icon
              name="ChatBubbleLeftEllipsisIcon"
              size={64}
              variant="solid"
              className="text-primary/10 absolute top-8 left-8"
            />

            <div className="relative z-10">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
                  <AppImage
                    src={activeTestimonial.image || DEFAULT_AVATAR}
                    alt={activeTestimonial.alt || `${activeTestimonial.name} avatar`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h4 className="text-xl font-heading font-bold text-textPrimary">
                    {activeTestimonial.name}
                  </h4>
                  <p className="text-sm font-body text-textSecondary">
                    {activeTestimonial.role ? `${activeTestimonial.role} • ` : ''}{activeTestimonial.area}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Icon
                    key={i}
                    name="StarIcon"
                    size={20}
                    variant="solid"
                    className={i < activeTestimonial.rating ? 'text-yellow-400' : 'text-gray-300'}
                  />
                ))}
              </div>

              <p className="text-lg font-body text-textPrimary leading-relaxed mb-4">
                &ldquo;{activeTestimonial.comment}&rdquo;
              </p>

              <p className="text-sm font-body text-textSecondary">{activeTestimonial.date}</p>
            </div>
          </div>

          <div className="flex justify-center space-x-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === activeIndex ? 'bg-primary w-8' : 'bg-gray-300'
                }`}
                aria-label={`View testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-12 max-w-5xl mx-auto">
          {testimonials.slice(0, 3).map((testimonial) => (
            <div
              key={testimonial.id}
              className="bg-card rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow duration-300"
            >
              <div className="flex items-center space-x-1 mb-3">
                {[...Array(5)].map((_, i) => (
                  <Icon
                    key={i}
                    name="StarIcon"
                    size={16}
                    variant="solid"
                    className={i < testimonial.rating ? 'text-yellow-400' : 'text-gray-300'}
                  />
                ))}
              </div>
              <p className="text-sm font-body text-textPrimary mb-3 line-clamp-3">
                &ldquo;{testimonial.comment}&rdquo;
              </p>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 rounded-full overflow-hidden">
                  <AppImage
                    src={testimonial.image || DEFAULT_AVATAR}
                    alt={testimonial.alt || `${testimonial.name} avatar`}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <p className="text-sm font-body font-semibold text-textPrimary">
                    {testimonial.name}
                  </p>
                  <p className="text-xs font-body text-textSecondary">{testimonial.area}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="max-w-5xl mx-auto mt-16">
          <h3 className="text-2xl md:text-3xl font-heading font-black text-textPrimary text-center mb-6">
            KENAPA PILIH SURILIPAT?
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="group bg-white rounded-2xl border border-purple-100 shadow-sm hover:shadow-md transition-all p-4 flex items-start gap-3">
              <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon name="CheckCircleIcon" size={18} variant="solid" className="text-primary" />
              </div>
              <div className="text-sm font-body text-textSecondary">
                Servis dari rumah, tanpa perlu ke dobi
              </div>
            </div>
            <div className="group bg-white rounded-2xl border border-purple-100 shadow-sm hover:shadow-md transition-all p-4 flex items-start gap-3">
              <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon name="CheckCircleIcon" size={18} variant="solid" className="text-primary" />
              </div>
              <div className="text-sm font-body text-textSecondary">Ejen komuniti setempat</div>
            </div>
            <div className="group bg-white rounded-2xl border border-purple-100 shadow-sm hover:shadow-md transition-all p-4 flex items-start gap-3">
              <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon name="CheckCircleIcon" size={18} variant="solid" className="text-primary" />
              </div>
              <div className="text-sm font-body text-textSecondary">
                Harga telus & dipersetujui awal
              </div>
            </div>
            <div className="group bg-white rounded-2xl border border-purple-100 shadow-sm hover:shadow-md transition-all p-4 flex items-start gap-3">
              <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon name="CheckCircleIcon" size={18} variant="solid" className="text-primary" />
              </div>
              <div className="text-sm font-body text-textSecondary">
                Tempahan mudah melalui WhatsApp
              </div>
            </div>
            <div className="group bg-white rounded-2xl border border-purple-100 shadow-sm hover:shadow-md transition-all p-4 flex items-start gap-3 sm:col-span-2 lg:col-span-1">
              <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon name="CheckCircleIcon" size={18} variant="solid" className="text-primary" />
              </div>
              <div className="text-sm font-body text-textSecondary">
                Sesuai untuk keluarga & individu sibuk
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto mt-16">
          <div className="bg-white rounded-2xl border border-purple-100 shadow-sm p-6 md:p-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                <Icon name="MapIcon" size={18} variant="outline" className="text-primary" />
              </div>
              <h3 className="text-2xl font-heading font-black text-textPrimary">
                KAMI BEROPERASI DI MALAYSIA
              </h3>
            </div>
            <p className="text-textSecondary font-body">
              Surilipat kini beroperasi di beberapa kawasan di Malaysia termasuk Selangor, Johor dan
              kawasan sekitarnya. Liputan akan terus diperluaskan mengikut penyertaan ejen baharu.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
